/**
 * Font loading utilities for Persian language support (bundled fonts only — no remote font CSS).
 */

import {
  LEARNER_PERSIAN_FONT_OPTIONS,
  type LearnerPersianFontId
} from '../constants/learnerPersianFonts';
import { applyLearnerPersianFont, readStoredLearnerPersianFont } from './applyLearnerPersianFont';

const ENGLISH_FONT_STACK = '"Inter", system-ui, -apple-system, "Segoe UI", Roboto, sans-serif';

/**
 * Check if the active Persian primary font appears loaded (best-effort).
 */
export const checkPersianFontsLoaded = (): boolean => {
  if (!document.fonts) {
    return false;
  }
  const id = readStoredLearnerPersianFont();
  if (id === 'system' || id === 'vazir') {
    return document.fonts.check('16px "Vazirmatn"');
  }
  const row = LEARNER_PERSIAN_FONT_OPTIONS.find((o) => o.id === id);
  const primary = row?.cssFamily ?? 'Vazirmatn';
  return document.fonts.check(`16px "${primary}"`);
};

/**
 * Wait for Persian fonts to load with timeout
 */
export const waitForPersianFonts = (timeout = 4000): Promise<boolean> => {
  return new Promise((resolve) => {
    if (checkPersianFontsLoaded()) {
      resolve(true);
      return;
    }

    const startTime = Date.now();

    const checkFonts = () => {
      if (checkPersianFontsLoaded()) {
        resolve(true);
        return;
      }

      if (Date.now() - startTime > timeout) {
        resolve(false);
        return;
      }

      requestAnimationFrame(checkFonts);
    };

    checkFonts();
  });
};

/**
 * Apply font loading class to document
 */
export const applyFontLoadingClass = (): void => {
  document.documentElement.classList.add('font-loading');

  waitForPersianFonts().then((loaded) => {
    document.documentElement.classList.remove('font-loading');
    if (loaded) {
      document.documentElement.classList.add('font-loaded');
    } else {
      document.documentElement.classList.add('font-fallback');
    }
  });
};

/**
 * Get appropriate font family based on text content
 */
export const getFontFamily = (text: string): string => {
  const persianRegex = /[\u0600-\u06FF\u0750-\u077F\u08A0-\u08FF\uFB50-\uFDFF\uFE70-\uFEFF]/;

  if (persianRegex.test(text)) {
    return 'var(--font-persian)';
  }

  return ENGLISH_FONT_STACK;
};

/**
 * Detect text direction based on content
 */
export const detectTextDirection = (text: string): 'rtl' | 'ltr' | 'auto' => {
  if (!text || text.trim().length === 0) {
    return 'auto';
  }

  const persianRegex = /[\u0600-\u06FF\u0750-\u077F\u08A0-\u08FF\uFB50-\uFDFF\uFE70-\uFEFF]/;
  const englishRegex = /[A-Za-z]/;

  const persianChars = (text.match(persianRegex) || []).length;
  const englishChars = (text.match(englishRegex) || []).length;

  if (persianChars > englishChars) {
    return 'rtl';
  } else if (englishChars > persianChars) {
    return 'ltr';
  }

  return 'auto';
};

/**
 * Format Persian numbers (convert English digits to Persian)
 */
export const formatPersianNumbers = (text: string): string => {
  const persianDigits = ['۰', '۱', '۲', '۳', '۴', '۵', '۶', '۷', '۸', '۹'];

  return text.replace(/[0-9]/g, (digit) => {
    return persianDigits[parseInt(digit, 10)];
  });
};

/**
 * Format English numbers (convert Persian digits to English)
 */
export const formatEnglishNumbers = (text: string): string => {
  const persianToEnglish: Record<string, string> = {
    '۰': '0', '۱': '1', '۲': '2', '۳': '3', '۴': '4',
    '۵': '5', '۶': '6', '۷': '7', '۸': '8', '۹': '9'
  };

  return text.replace(/[۰-۹]/g, (digit) => {
    return persianToEnglish[digit] || digit;
  });
};

/**
 * Initialize font loading system (bundled @fontsource faces; preference from localStorage).
 */
export const initializeFontSystem = (): void => {
  const id: LearnerPersianFontId = readStoredLearnerPersianFont();
  applyLearnerPersianFont(id);

  document.documentElement.style.setProperty('--font-english', ENGLISH_FONT_STACK);

  applyFontLoadingClass();

  void (async () => {
    const row = LEARNER_PERSIAN_FONT_OPTIONS.find((o) => o.id === id);
    const primary = id === 'system' || id === 'vazir' ? 'Vazirmatn' : (row?.cssFamily ?? 'Vazirmatn');
    try {
      if (document.fonts?.load) {
        await document.fonts.load(`400 16px "${primary}"`);
      }
    } catch {
      /* ignore */
    }
  })();
};
